/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors, UserSelectors } from '@recruitee/account-data';
import type { CurrentUser, User, UserReference } from '@recruitee/account-types';
import type { FullCandidate, ShareCandidateWithTeamResponse } from '@recruitee/candidate-types';
import {
  CorrelationId,
  createEntityCollectionStore,
  createSelectorDeepEqual,
  EntityState,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  PickActions,
  Reference,
} from '@recruitee/ngrx';
import { PlacementSelectors } from '@recruitee/placements-data';
import type { Placement } from '@recruitee/placements-types';
import { CandidateReferrerSelectors } from '@recruitee/referrals-data';

import { AccessToCandidatePolicy, AtcGroupedUsers } from '../types';
import {
  CopyToCompanyParams,
  DeleteParsedCvParams,
  SaveParams,
  ShareCandidateWithTeamRequest,
  UpdateFileParams,
  UpdateParseCvParams,
} from './full-candidate-http-client';

const idSelector = ({ id }: { id: number }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<FullCandidate>()(
  'FullCandidate',
  {
    idSelector,
    references: makeRef =>
      <Array<Reference<FullCandidate>>>[
        makeRef('adminId', 'admin', UserSelectors.byId),
        makeRef('adminIds', 'admins', UserSelectors.byIds),
        makeRef('placements', 'placements', (placements: Placement[]) =>
          PlacementSelectors.byIdsWithReferences((placements || []).map(({ id }) => id)),
        ),
        makeRef('referralReferrersIds', 'referralReferrers', CandidateReferrerSelectors.byIds),
      ],
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      const fetchOne = (state: EntityState<FullCandidate>, payload: { id: number }) =>
        defaultActions.fetchOne(state, payload);
      const fetchOneSuccess = (
        state: EntityState<FullCandidate>,
        payload: { data: FullCandidate },
      ) => defaultActions.fetchOneSuccess(state, payload);
      const fetchOneFailed = (
        state: EntityState<FullCandidate>,
        payload: { error: HttpErrorResponse; id: number },
      ) => defaultActions.fetchOneFailed(state, payload);

      return {
        fetchOne: fetchOne,
        fetchOneSuccess: fetchOneSuccess,
        fetchOneFailed: fetchOneFailed,

        bufferedFetchOne: fetchOne,

        createOnePessimistic: (state, payload: { params: SaveParams } & Required<CorrelationId>) =>
          adapter.markAsPending(state, {
            entities: [payload.correlationId],
            scope: null,
          }),

        updateOnePessimistic: (state, payload: { params: SaveParams }) =>
          defaultActions.updateOnePessimistic(state, { data: { id: payload.params.data.id! } }),

        removePhoto: fetchOne,
        removePhotoSuccess: fetchOneSuccess,
        removePhotoFailed: fetchOneFailed,

        updateCv: (state, payload: UpdateFileParams) => fetchOne(state, payload),
        updateCvSuccess: fetchOneSuccess,
        updateCvFailed: fetchOneFailed,

        updateParseCv: (state, payload: UpdateParseCvParams) => fetchOne(state, payload),
        updateParseCvSuccess: fetchOneSuccess,
        updateParseCvFailed: fetchOneFailed,

        deleteCv: fetchOne,
        deleteCvSuccess: fetchOneSuccess,
        deleteCvFailed: fetchOneFailed,

        deleteParsedCv: (state, payload: DeleteParsedCvParams) => fetchOne(state, payload),
        deleteParsedCvSuccess: fetchOneSuccess,
        deleteParsedCvFailed: fetchOneFailed,

        reparseCv: fetchOne,
        reparseCvSuccess: fetchOneSuccess,
        reparseCvFailed: fetchOneFailed,

        updateCoverLetterFile: (state, payload: UpdateFileParams) => fetchOne(state, payload),
        updateCoverLetterFileSuccess: fetchOneSuccess,
        updateCoverLetterFileFailed: fetchOneFailed,

        deleteCoverLetterFile: (state, payload: { candidateId: number }) =>
          fetchOne(state, { id: payload.candidateId }),
        deleteCoverLetterFileSuccess: fetchOneSuccess,
        deleteCoverLetterFileFailed: fetchOneFailed,

        coverLetterPromptReplaceExistingFile: ngrxEvent<void>(),
        coverLetterPromptReplaceExistingFileSuccess: ngrxEvent<void>(),
        coverLetterPromptReplaceExistingFileFailed: ngrxEvent<void>(),

        coverLetterPromptReplaceTextWithFile: ngrxEvent<void>(),
        coverLetterPromptReplaceTextWithFileSuccess: ngrxEvent<void>(),
        coverLetterPromptReplaceTextWithFileFailed: ngrxEvent<void>(),

        coverLetterPromptDelete: ngrxEvent<void>(),
        coverLetterPromptDeleteSuccess: ngrxEvent<void>(),
        coverLetterPromptDeleteFailed: ngrxEvent<void>(),

        view: fetchOne,
        viewSuccess: fetchOneSuccess,
        viewFailed: fetchOneFailed,

        merge: ngrxEvent<{ id: number }>(),
        mergeSuccess: ngrxEvent<{ uuid: string }>(),
        mergeFailed: ngrxEvent<{ error: HttpErrorResponse; id: number }>(),

        follow: fetchOne,
        followSuccess: fetchOneSuccess,
        followFailed: fetchOneFailed,

        unfollow: fetchOne,
        unfollowSuccess: fetchOneSuccess,
        unfollowFailed: fetchOneFailed,

        addNewFollowers: (state, payload: { id: number; userIds: number[] }) =>
          fetchOne(state, payload),
        addNewFollowersSuccess: (
          state,
          payload: {
            candidate: {
              adminIds: number[];
              id: number;
            };
          },
        ) =>
          state.entityMap[payload.candidate.id]
            ? adapter.updateOne(state, {
                ...payload.candidate,
                adminIds: payload.candidate.adminIds,
              })
            : state,
        addNewFollowersFailed: ngrxEvent<{ error: HttpErrorResponse; id: number }>(),

        removeFollower: (state, payload: { id: number; userId: number }) =>
          fetchOne(state, payload),
        removeFollowerSuccess: (
          state,
          payload: {
            candidate: {
              adminIds: number[];
              id: number;
            };
          },
        ) =>
          state.entityMap[payload.candidate.id]
            ? adapter.updateOne(state, {
                ...payload.candidate,
                adminIds: payload.candidate.adminIds,
              })
            : state,
        removeFollowerFailed: ngrxEvent<{ error: HttpErrorResponse; id: number }>(),

        retrieve: fetchOne,
        retrieveSuccess: fetchOneSuccess,
        retrieveFailed: fetchOneFailed,

        copyToCompany: (
          state,
          payload: { params: CopyToCompanyParams } & Required<CorrelationId>,
        ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
        copyToCompanySuccess: defaultActions.createOnePessimisticSuccess,
        copyToCompanyFailed: defaultActions.createOnePessimisticFailed,

        shareWithTeam: ngrxEvent<ShareCandidateWithTeamRequest & { admins: UserReference[] }>(),
        shareWithTeamSuccess: (
          state,
          payload: ShareCandidateWithTeamResponse['candidate'] & {
            id: number;
            sharedWith: User[];
            newUsers: User[];
          },
        ) =>
          state.entityMap[payload.id]
            ? adapter.updateOne(state, {
                ...payload,
                adminIds: payload.newUsers.map(u => u.id),
                admins: payload.newUsers.map(u => ({ ...u, type: 'Admin' })),
              })
            : state,

        shareWithTeamFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        revokeUserAccess: ngrxEvent<{ candidateId: number; userId: number; admins: User[] }>(), // Admins needed to create new set of followers
        revokeUserAccessSuccess: (
          state,
          payload: {
            userId: number;
            id: number;
            sharedAdminCount: number;
            sharedContainerCount: number;
            newUsers: User[];
          },
        ) =>
          adapter.updateOne(state, {
            ...payload,
            admins: payload.newUsers.map(u => ({ ...u, type: 'Admin' })),
            adminIds: payload.newUsers.map(u => u.id),
          }),
        revokeUserAccessFailed: ngrxEvent<
          {
            error: HttpErrorResponse;
          } & CorrelationId
        >(),
        updateShareCounts: (
          state,
          payload: { id: number; sharedAdminCount: number; sharedContainerCount: number },
        ) => adapter.updateOne(state, payload),
      };
    },
  },
);

const FullCandidateActions: PickActions<
  typeof actions,
  | 'fetchOne'
  | 'updateOnePessimistic'
  | 'createOnePessimistic'
  | 'removePhoto'
  | 'updateCv'
  | 'updateParseCv'
  | 'deleteCv'
  | 'deleteParsedCv'
  | 'reparseCv'
  | 'view'
  | 'merge'
  | 'follow'
  | 'unfollow'
  | 'addNewFollowers'
  | 'removeFollower'
  | 'retrieve'
  | 'copyToCompany'
  | 'updateCoverLetterFile'
  | 'deleteCoverLetterFile'
  | 'coverLetterPromptReplaceExistingFile'
  | 'coverLetterPromptReplaceTextWithFile'
  | 'coverLetterPromptDelete'
  | 'shareWithTeam'
  | 'revokeUserAccess'
> &
  Pick<typeof actions, 'bufferedFetchOne' | 'updateShareCounts'> = actions;

// Tagging fns
const userTaggingCommonFn = (user: User, candidate: FullCandidate) => {
  const canViewCandidates = user.roleAbilities.includes(Ability.ViewCandidates);
  if (!canViewCandidates) return false;

  const canViewAllJobs = user.roleAbilities.includes(Ability.ViewAllJobs);
  if (canViewAllJobs) return true;

  if (!!candidate.placements?.length) {
    return (
      (!!user.offerIds?.length || !!user.accessiblePlacementIds?.length) &&
      candidate.placements.some(placement => {
        const placementOfferId = <number>(placement.offerId || placement.talentPoolId);
        return (
          user.offerIds?.includes(placementOfferId) ||
          (user.accessiblePlacementIds || []).includes(placement.id)
        );
      })
    );
  }

  return false;
};

const userTaggingViewCandidatesFn = (user: User) =>
  user.roleAbilities.includes(Ability.ViewCandidates);

const userTaggingOfferFn = (
  user: User,
  candidate: FullCandidate | null,
  offerId: number | null,
) => {
  const canViewCandidates = user.roleAbilities.includes(Ability.ViewCandidates);
  if (!!candidate && !canViewCandidates) return false;

  if (user.viewAllJobs) return true;

  const canAccessOffer = !!offerId && user.offerIds?.includes(offerId);
  if (canAccessOffer) return true;

  const canAccessPlacement =
    !!candidate &&
    !!offerId &&
    user.accessiblePlacementIds?.includes(
      candidate.placements?.find(placement => placement.offerId === offerId)?.id ?? -1,
    );

  return !!canAccessPlacement;
};

const taggedUsers = createSelector(UserSelectors.entities(), (users: User[]) =>
  users.map(user => ({ ...user, hasCandidateAccess: true })),
);

const tagUsersWithViewHiredCandidates = (
  users: User[],
  candidate: FullCandidate | null,
  offerId?: number | null,
) =>
  candidate
    ? users.map(user => ({
        ...user,
        hasCandidateAccess:
          user.roleAbilities.includes(Ability.ViewHiredCandidates) &&
          (!!offerId
            ? userTaggingOfferFn(user, candidate, offerId)
            : userTaggingCommonFn(user, candidate)),
      }))
    : users;
const tagUsersWithCandidateId = (users: User[], candidate: FullCandidate | null) =>
  candidate
    ? users.map(user => ({
        ...user,
        hasCandidateAccess: userTaggingCommonFn(user, candidate),
      }))
    : users;
const tagUsersWithAccessBasedOnHiringStatus = (users: User[], candidate: FullCandidate | null) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagUsersWithViewHiredCandidates(users, candidate)
    : tagUsersWithCandidateId(users, candidate);

const tagUsersWithAccessToOfferOrCandidate = (
  users: User[],
  candidate: FullCandidate | null,
  offerId: number,
) =>
  users.map(user => ({
    ...user,
    hasCandidateAccess: userTaggingOfferFn(user, candidate, offerId),
  }));

const tagUsersForOffer = (users: User[], candidate: FullCandidate | null, offerId: number) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagUsersWithViewHiredCandidates(users, candidate, offerId)
    : tagUsersWithAccessToOfferOrCandidate(users, candidate, offerId);

const commonUsersTagged = memoizeSelector((candidateId: number) =>
  createSelector(taggedUsers, selectors.byIdWithReferences(candidateId), (users, candidate) =>
    tagUsersWithAccessBasedOnHiringStatus(users, candidate),
  ),
);

const offerUsersTagged = memoizeSelector(
  (props: { candidateId?: number; offerId?: number | null }) =>
    createSelector(
      UserSelectors.entities(),
      props.candidateId ? selectors.byIdWithReferences(props.candidateId) : () => null,
      (users: User[], candidate: FullCandidate) =>
        props.offerId
          ? tagUsersForOffer(users, candidate, props.offerId)
          : tagUsersWithAccessBasedOnHiringStatus(users, candidate),
    ),
);

const filterTaggedUsers = (users: User[], hasCandidateAccess: boolean) =>
  users.filter(user => user.hasCandidateAccess === hasCandidateAccess);

// Selectors for tagged users
const commonUsersWithAccess = memoizeSelector((candidateId: number) =>
  createSelector(commonUsersTagged(candidateId), (users: User[]) => filterTaggedUsers(users, true)),
);
const commonUsersWithoutAccess = memoizeSelector((candidateId: number) =>
  createSelector(commonUsersTagged(candidateId), (users: User[]) =>
    filterTaggedUsers(users, false),
  ),
);

const commonUsersGrouped = memoizeSelector((candidateId: number) =>
  createSelector(
    commonUsersWithAccess(candidateId),
    commonUsersWithoutAccess(candidateId),
    (usersWithAccess, usersWithoutAccess) => ({
      usersWithAccess: usersWithAccess,
      usersWithoutAccess: usersWithoutAccess,
    }),
  ),
);

const offerUsersWithAccess = memoizeSelector(
  (props: { candidateId?: number; offerId?: number | null }) =>
    createSelector(offerUsersTagged(props), (users: User[]) => filterTaggedUsers(users, true)),
);

const offerUsersWithoutAccess = memoizeSelector(
  (props: { candidateId?: number; offerId?: number | null }) =>
    createSelector(offerUsersTagged(props), (users: User[]) => filterTaggedUsers(users, false)),
);

const offerUsersGrouped = memoizeSelector(
  (props: { candidateId?: number; offerId?: number | null }) =>
    createSelector(
      offerUsersWithAccess(props),
      offerUsersWithoutAccess(props),
      (usersWithAccess, usersWithoutAccess) => ({
        usersWithAccess: usersWithAccess,
        usersWithoutAccess: usersWithoutAccess,
      }),
    ),
);

const usersSharedWith = memoizeSelector((props: { candidateId: number }) =>
  createSelector(
    UserSelectors.entities(),
    selectors.byIdWithReferences(props.candidateId),
    (users: User[], candidate: FullCandidate) =>
      users.filter(user =>
        (candidate?.placements || []).some(
          placement => user.accessiblePlacementIds?.includes(placement.id),
        ),
      ),
  ),
);

const currentUserAvailablePlacements = memoizeSelector((props: { candidateId?: number }) =>
  createSelector(
    CurrentUserSelectors.data,
    props.candidateId ? selectors.byIdWithReferences(props.candidateId) : () => null,
    (currentUser: CurrentUser, candidate: FullCandidate | null) =>
      candidate?.placements.filter(placement => {
        const placementOfferId = <number>(placement.offerId || placement.talentPoolId);
        return (
          currentUser.roleAbilities.includes(Ability.ViewAllJobs) ||
          currentUser.membership?.offerIds?.includes(placementOfferId) ||
          currentUser.membership?.accessiblePlacementIds?.includes(placement.id)
        );
      }) ?? [],
  ),
);

const usersTaggedByCandidateViewAbility = createSelector(UserSelectors.entities(), users =>
  users.map(user => ({ ...user, hasCandidateAccess: userTaggingViewCandidatesFn(user) })),
);

const usersWithCandidateViewAbility = createSelector(usersTaggedByCandidateViewAbility, users =>
  filterTaggedUsers(users, true),
);

const usersWithoutCandidateViewAbility = createSelector(usersTaggedByCandidateViewAbility, users =>
  filterTaggedUsers(users, false),
);

const usersGroupedByCandidateViewAbility = createSelector(
  usersWithCandidateViewAbility,
  usersWithoutCandidateViewAbility,
  (usersWithAccess, usersWithoutAccess) => ({
    usersWithAccess,
    usersWithoutAccess,
  }),
);

const usersShareableOnTheGoForHiredCandidate = ({
  usersWithAccess,
  usersWithoutAccess,
}: AtcGroupedUsers) => ({
  usersWithAccess: usersWithAccess.filter(user =>
    user.roleAbilities.includes(Ability.ViewHiredCandidates),
  ),
  usersWithoutAccess: usersWithoutAccess
    .concat(
      usersWithAccess.filter(user => !user.roleAbilities.includes(Ability.ViewHiredCandidates)),
    )
    .map(user => ({ ...user, hasCandidateAccess: false })),
});

export const usersShareableOnTheGo = memoizeSelector(
  (props: {
    candidateId?: number;
    offerId?: number | null;
    atcPolicy: AccessToCandidatePolicy;
  }) => {
    const commonOrOfferUsersGrouped =
      props.atcPolicy === AccessToCandidatePolicy.Common || !props.offerId
        ? commonUsersGrouped(props.candidateId!)
        : props.atcPolicy === AccessToCandidatePolicy.Offer
          ? offerUsersGrouped(props)
          : () => <AtcGroupedUsers>{ usersWithAccess: [], usersWithoutAccess: [] };

    return createSelector(
      AbilitiesSelectors.hasOne(Ability.ShareCandidates),
      usersGroupedByCandidateViewAbility,
      commonOrOfferUsersGrouped,
      selectors.byIdWithReferences(props.candidateId ?? -1),
      (
        canShareCandidates,
        usersGroupedByCandidateViewAbility,
        commonOrOfferUsersGrouped,
        candidate,
      ) => {
        if (canShareCandidates && !!candidate?.placements?.some(p => !!p.hiredById)) {
          return usersShareableOnTheGoForHiredCandidate(usersGroupedByCandidateViewAbility);
        } else if (canShareCandidates && !!props.offerId && !!candidate?.placements?.length) {
          return usersGroupedByCandidateViewAbility;
        }

        return commonOrOfferUsersGrouped;
      },
    );
  },
);

const groupedUsersInShareActionContext = memoizeSelector(
  (props: {
    candidateId?: number;
    offerId?: number | null;
    atcPolicy: AccessToCandidatePolicy;
  }) => {
    if (props.atcPolicy === AccessToCandidatePolicy.NoCandidateViewAbility) {
      return usersGroupedByCandidateViewAbility;
    }

    if (props.atcPolicy === AccessToCandidatePolicy.Common && !props.candidateId) {
      return createSelector(UserSelectors.entities(), commonUsers => ({
        usersWithAccess: commonUsers,
        usersWithoutAccess: <User[]>[],
      }));
    }

    return usersShareableOnTheGo(props);
  },
);

const anonymousCandidateStages = memoizeSelector((props: { candidateId?: number }) =>
  createSelector(
    selectors.byIdWithReferences(props.candidateId ?? -1),
    candidate =>
      candidate?.placements
        .filter(placement => placement.stage?.candidateAnonymizationEnabled)
        .map(placement => placement.stage) ?? [],
  ),
);

const FullCandidateSelectors = {
  ...selectors,
  byIdWithReferences: (id: number) =>
    createSelectorDeepEqual(selectors.byIdWithReferences(id), state => state),
  commonUsersGrouped: commonUsersGrouped,
  offerUsersWithAccess: offerUsersWithAccess,
  offerUsersGrouped: offerUsersGrouped,
  usersSharedWith,
  currentUserAvailablePlacements: currentUserAvailablePlacements,
  groupedUsersInShareActionContext,
  anonymousCandidateStages,
};

export {
  FullCandidateActions,
  FullCandidateSelectors,
  reducer as FullCandidateReducer,
  namespace as FullCandidateNamespace,
  selectors as FullCandidateSelectorsRaw,
};
